<template>
    <div class="transactions-list__wrapper">
      <div v-if="loading" style="padding: 24px 0">
        <BaseLoader />
      </div>
      <template v-else-if="transactionList.length">
        <div class="transactions-header">
          <div class="title">{{ title }}</div>
          <DateRangePicker v-model="dates" />
        </div>
        <div
          v-for="(item, idx) in transactionListPaginated"
          :key="idx"
          class="transaction"
          @click="showTransactionInfo(item)"
        >
          <TransactionTypesIcon :type="type(item)" />
          <div class="text">
            <div class="left-side">
              <div class="type-and-status">
                <div class="name">{{ text(`transactions.list.${type(item)}`) }}</div>
                <div class="status" :class="item.status">{{ text(`transactions.list.status.${item.status}`) }}</div>
              </div>
              <div v-if="type(item) === 'exchange'" class="source-wallet">
                {{ exchangeWalletLabel(item) }}
              </div>
              <div v-else class="source-wallet">
                {{ item.currency_code }} {{ text('transactions.list.details.transaction.wallet') }}
              </div>
            </div>
            <div class="right-side">
              <template v-if="type(item) === 'exchange'">
                <div class="amount exchange-amount">
                  <span class="minus">
                    <CurrencyAmount
                      v-if="exchangeSide(item, 'from')"
                      :amount="exchangeSide(item, 'from').amount"
                      :currency-code="exchangeSide(item, 'from').currency_code"
                      prefix="-"
                    />
                    <template v-else>-</template>
                  </span>
                  <span class="exchange-arrow">-&gt;</span>
                  <span class="plus">
                    <CurrencyAmount
                      v-if="exchangeSide(item, 'to')"
                      :amount="exchangeSide(item, 'to').amount"
                      :currency-code="exchangeSide(item, 'to').currency_code"
                      prefix="+"
                    />
                    <template v-else>-</template>
                  </span>
                </div>
              </template>
              <template v-else-if="item.status !== 'partial_complete'">
                <div class="amount" :class="type(item) === 'withdrawal' ? 'minus' : 'plus'">
                  <CurrencyAmount :amount="item.amount" :currency-code="item.currency_code" />
                </div>
              </template>
              <div v-else class="partial-complete">
                <div class="amount-paid">
                  <span>
                    {{ text('transactions.list.status.paid') }}:
                  </span>
                  <div class="amount" :class="type(item) === 'withdrawal' ? 'minus' : 'plus'">
                    <CurrencyAmount :amount="item.amount_paid" :currency-code="item.currency_code" />
                    <span>/</span>
                    <CurrencyAmount :amount="item.amount" :currency-code="item.currency_code" />
                  </div>
                </div>
              </div>
              <div class="time">{{ formatDateTime(item.created_at) }}</div>
            </div>
          </div>
        </div>
        <div class="transactions-pagination-block">
          <Pagination v-model="currentPage" :total="transactionListFiltered.length" :items-per-page="itemsPerPage" />
        </div>
      </template>
      <div v-else class="empty" >
          <TransactionsEmptyState />
      </div>
      <Sidebar v-model="showSidebar" :title="text('transactions.list.details.title')">
        <template #body>
          <div  v-if="selectedTransaction" class="transaction-info-wrapper">
            <div>
              {{ text('transactions.list.details.transaction') }}
            </div>
            <div class="layout">
              <div class="row">
                <div class="label">{{ text('transactions.list.details.transaction.id') }}</div>
                <div class="value">{{ shortenString(selectedTransaction.id) }} <copy :value="selectedTransaction.id" /> </div>
              </div>
              <div class="row">
                <div class="label">{{ text('transactions.list.details.transaction.type') }}</div>
                <div class="value">
                  {{ text(`transactions.list.${type(selectedTransaction)}`) }}
                  <TransactionTypesIcon :type="type(selectedTransaction)" transparent />
                </div>
              </div>
              <div class="row">
                <div class="label">{{ text('transactions.list.details.transaction.status') }}</div>
                <div class="value">
                  <div class="status" :class="selectedTransaction.status">
                    <svg  v-if="['partial_complete', 'complete'].includes(selectedTransaction.status)" xmlns="http://www.w3.org/2000/svg" width="14" height="10" viewBox="0 0 14 10" fill="none">
                      <path d="M12.3332 1L4.99984 8.33333L1.6665 5" stroke="#1B1B1B" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <svg v-if="['failed'].includes(selectedTransaction.status)" xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 14 14" fill="none">
                      <path d="M1.4 14L0 12.6L5.6 7L0 1.4L1.4 0L7 5.6L12.6 0L14 1.4L8.4 7L14 12.6L12.6 14L7 8.4L1.4 14Z" fill="#1B1B1B"/>
                    </svg>
                    {{ text(`transactions.list.status.${selectedTransaction.status}`) }}
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="label">{{ text('transactions.list.details.transaction.date') }}</div>
                <div class="value">{{ formatDateTime(selectedTransaction.created_at) }}</div>
              </div>
              <template v-if="type(selectedTransaction) === 'exchange'">
                <div class="row">
                  <div class="label">{{ text('exchange.from') }}</div>
                  <div class="value amount minus">
                    <CurrencyAmount
                      v-if="exchangeSide(selectedTransaction, 'from')"
                      :amount="exchangeSide(selectedTransaction, 'from').amount"
                      :currency-code="exchangeSide(selectedTransaction, 'from').currency_code"
                      prefix="-"
                    />
                    <template v-else>-</template>
                  </div>
                </div>
                <div class="row">
                  <div class="label">{{ text('exchange.to') }}</div>
                  <div class="value amount plus">
                    <CurrencyAmount
                      v-if="exchangeSide(selectedTransaction, 'to')"
                      :amount="exchangeSide(selectedTransaction, 'to').amount"
                      :currency-code="exchangeSide(selectedTransaction, 'to').currency_code"
                    />
                    <template v-else>-</template>
                  </div>
                </div>
              </template>
              <template v-else>
              <div class="row">
                <div class="label">{{ text('transactions.list.details.transaction.currency') }}</div>
                <div class="value">
                  {{ selectedTransaction.currency_code }}
                  <span class="secondary">
                    <CurrencyAmount :currency-code="selectedTransaction.currency_code" />
                  </span>
                </div>
              </div>
              <div class="row">
                <div class="label">{{ text('transactions.list.details.transaction.amount') }}</div>
                <div v-if="selectedTransaction.status !== 'partial_complete'" class="value amount" :class="type(selectedTransaction) === 'withdrawal' ? 'minus' : 'plus'">
                  <CurrencyAmount :amount="selectedTransaction.amount" :currency-code="selectedTransaction.currency_code" />
                </div>
                <div v-else class="value amount-paid amount" :class="type(selectedTransaction) === 'withdrawal' ? 'minus' : 'plus'">
                  <CurrencyAmount :amount="selectedTransaction.amount_paid" :currency-code="selectedTransaction.currency_code" />
                  <span>/</span>
                  <CurrencyAmount :amount="selectedTransaction.amount" :currency-code="selectedTransaction.currency_code" />
                </div>
              </div>
              </template>
              <div v-if="selectedTransaction.commission" class="row">
                <div class="label">{{ text('transactions.list.details.transaction.commission') }}</div>
                <div class="value">
                  <CurrencyAmount :amount="selectedTransaction.commission" :currency-code="selectedTransaction.currency_code" />
                </div>
              </div>
              <div v-if="selectedTransaction.final_amount" class="row">
                <div class="label">{{ text('transactions.list.details.transaction.final_amount') }}</div>
                <div class="value">
                  <CurrencyAmount :amount="selectedTransaction.final_amount" :currency-code="selectedTransaction.currency_code" />
                </div>
              </div>
            </div>
<!--            <div class="mt-5">-->
<!--              {{ text('transactions.list.details.method') }}-->
<!--            </div>-->
<!--            <div class="layout">-->
<!--              <div class="row">-->
<!--                <div class="label">{{ text('transactions.list.details.method.network') }}</div>-->
<!--                <div class="value">USDT TRC20</div>-->
<!--              </div>-->
<!--              <div class="row">-->
<!--                <div class="label">{{ text('transactions.list.details.method.address') }}</div>-->
<!--                <div class="value">{{ shortenString('bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh') }} <copy :value="'bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh'" /></div>-->
<!--              </div>-->
<!--              <div class="row">-->
<!--                <div class="label">{{ text('transactions.list.details.method.confirmation') }}</div>-->
<!--                <div class="value">Выполнено</div>-->
<!--              </div>-->
<!--            </div>-->
          </div>
        </template>
      </Sidebar>
    </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { shortenString } from "@/utils/strings.js"

export default {
    name: "Transactions",
    components: {
      BaseLoader: defineAsyncComponent (() => import(/* webpackChunkName: "components/base/BaseLoader" */ '@/components/base/BaseLoader.vue')),
      Copy: defineAsyncComponent (() => import(/* webpackChunkName: "components/base/clipboardCopy/copy" */ '@/components/base/clipboardCopy/copy.vue')),
      TransactionsEmptyState: defineAsyncComponent (() => import(/* webpackChunkName: "components/transactions/TransactionsEmptyState" */ '@/components/transactions/TransactionsEmptyState.vue')),
      TransactionTypesIcon: defineAsyncComponent (() => import(/* webpackChunkName: "components/transactions/icons/Types" */ './icons/Types.vue')),
      CurrencyAmount: defineAsyncComponent (() => import(/* webpackChunkName: "components/base/CurrencyAmount" */ '@/components/base/CurrencyAmount.vue')),
      DateRangePicker: defineAsyncComponent (() => import(/* webpackChunkName: "components/DateRangePicker" */ '@/components/DateRangePicker.vue')),
      Pagination: defineAsyncComponent (() => import(/* webpackChunkName: "components/base/pagination" */ '@/components/base/pagination/index.vue')),
      Sidebar: defineAsyncComponent (() => import(/* webpackChunkName: "components/base/Sidebar" */ '@/components/base/Sidebar.vue')),
    },
    props: {
        title: {
            type: String,
            default: ''
        }
    },
    data () {
        return {
            dates: [],
            transactionList: [],
            selectedTransaction: null,
            currentPage: 1,
            itemsPerPage: 10,
            loading: false,
            showSidebar: false
        }
    },
    created() {
        this.getTransactionList()
    },
    computed: {
      authToken() {
          return this.$store.state.authToken
      },
      transactionListFiltered({ dates }) {
        return this.transactionList.filter((item) => {
          return dates[0] < new Date(item.created_at) && dates[1] > new Date(item.created_at)
        })
      },
      transactionListPaginated({ transactionListFiltered , currentPage, itemsPerPage }) {
        const start = (currentPage - 1) * itemsPerPage
        return transactionListFiltered.slice(start, start + itemsPerPage)
      }
    },
    methods: {
        shortenString,
        type(transaction) {
          if (transaction.type) {
            return transaction.type
          }
          if (Number(transaction.amount) === 0) {
            return 'operation'
          } else if (Number(transaction.amount) > 0) {
            return 'topup'
          } else if (Number(transaction.amount) < 0) {
            return 'withdrawal'
          }
        },
        exchangeSide(transaction, side) {
          const exchangeSide = transaction.exchange?.[side]
          if (!exchangeSide || exchangeSide.amount === null || !exchangeSide.currency_code) {
            return null
          }

          return exchangeSide
        },
        exchangeWalletLabel(transaction) {
          const fromCurrency = transaction.exchange?.from?.currency_code || '-'
          const toCurrency = transaction.exchange?.to?.currency_code || '-'

          return `${fromCurrency} -> ${toCurrency}`
        },
        async getTransactionList() {
            this.loading = true
            const { data } = await this.$http.get(
                '/v2/wallet/transaction?ordering=-created_at',
                {
                    "headers": {
                        "Authorization": "Token " + this.authToken
                    }
                }
            )

            this.loading = false
            this.transactionList = data.results;
        },
        showTransactionInfo(item) {
          this.selectedTransaction = item
          this.showSidebar = true
        }
    }
}
</script>

<style lang="scss" scoped>
  .transactions-list__wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;

    border-radius: 16px;
    padding: 16px;
    background: var(--element-bg);

    .empty {
      height: calc(100dvh - 934px);
      min-height: 200px;
    }

    .transactions-header {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .title {
        padding: 0 0 10px 0;
        color: #FFF;
        font-family: "Rubik", sans-serif;
        font-size: 18px;
        font-style: normal;
        font-weight: 500;
        line-height: normal;
        letter-spacing: 0.36px;
      }
    }
  }

  .transaction {
    display: flex;
    align-items: center;
    padding: 8px 0;
    gap: 12px;

    cursor: pointer;

    &:not(:last-child) {
      border-width: 0 0 1px 0;
      border-style: solid;
      border-color: var(--border-color);
    }
  }
  .transaction img {
    width: 38px;
    height: 38px;
    -o-object-fit: cover;
    object-fit: cover;
  }
  .transaction .text {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .left-side {
      display: flex;
      flex-direction: column;
      gap: 5px;

      .type-and-status {
        display: flex;
        align-items: end;
        gap: 5px;

        .name {
          font-size: 16px;
        }
        .status {
          font-size: 12px;
          font-weight: 500;
          padding-bottom: 2px;

          &.failed {
            color: #F34B47;
          }
          &.complete {
            color: #00BF8E;
          }
          &.partial_complete {
            color: #00BF8E;
          }
          &.pending {
            color: #CCCC00;
          }
        }
      }
      .source-wallet {
        font-size: 12px;
        color: var(--text-color-secondary);
      }
    }
    .right-side {
      display: flex;
      flex-direction: column;
      gap: 5px;
      align-items: end;

      .partial-complete {
        display: flex;
        align-items: center;
        gap: 8px;

        .amount-paid {
          display: flex;
          align-items: end;
          gap: 8px;
          span {
            font-size: 14px;
          }
        }
      }
      .amount {
        font-size: 16px;

        &.exchange-amount {
          display: flex;
          align-items: center;
          gap: 6px;
          white-space: nowrap;

          .minus {
            color: #F34B47;
          }

          .plus {
            color: #00BF8E;
          }

          .exchange-arrow {
            color: var(--text-color-secondary);
          }
        }

        &.plus {
          color: #00BF8E;
          &::before {
            content: "+";
          }
        }
        &.minus {
          color: #F34B47;
          &::before {
            content: "";
          }
        }
      }
      .time {
        color: var(--text-color-secondary);
        font-size: 12px;
      }
    }
  }

  .transactions-pagination-block {
    padding: 15px 0 5px;
  }

  .transaction-info-wrapper {
    font-size: 16px;
    font-weight: 400;

    .layout {
      margin-top: 5px;
      padding: 0 16px;
      border-radius: 12px;

      background: var(--element-bg);

      .row {
        display: flex;
        align-items: center;
        justify-content: space-between;

        padding: 12px 0;
        border-bottom: 1px solid var(--border-color);

        &:last-child {
          border-bottom: none;
        }

        .label {
          color: var(--text-color-secondary);
        }
        .value {
          display: flex;
          align-items: center;
          gap: 5px;

          .secondary {
            color: var(--text-color-secondary);
          }

          .status {
            display: flex;
            align-items: center;
            gap: 5px;

            padding: 6px;
            border-radius: 6px;

            color: var(--text-color-primary-reverse);

            &.failed {
              background-color: #F34B47;
            }
            &.complete {
              background-color: #00BF8E;
            }
            &.partial_complete {
              background-color: #00BF8E;
            }
            &.pending {
              background-color: #CCCC00;
            }
          }
          &.amount {
            font-size: 16px;

            &.plus {
              color: #00BF8E;
              &::before {
                content: "+";
              }
            }
            &.minus {
              color: #F34B47;
              &::before {
                content: "";
              }
            }
          }
          &.amount-paid {
            display: flex;
            align-items: end;
            gap: 8px;
            span {
              font-size: 14px;
            }
          }
        }
      }
    }
  }

  @media (max-width: 920px) {
    .transactions-list__wrapper {
      background: var(--layout-bg);
    }
    .transactions-list__wrapper .transaction .text .text_bottom .time {
      font-size: 12px;
    }
  }
</style>
